// src/services/seoService.ts
import { sitemapGenerator } from '../utils/sitemapGenerator';

interface PageSEO {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url: string;
  type?: 'website' | 'article' | 'video' | 'audio';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  noIndex?: boolean;
}

class SEOService {
  private baseUrl = import.meta.env.VITE_NODE_ENV === 'production' 
    ? 'https://strymhub.com' 
    : 'http://localhost:5173';
  
  // Page-specific SEO configurations
  getPageSEO(page: string, data?: any): PageSEO {
    const baseSEO = {
      url: `${this.baseUrl}${page}`,
      keywords: 'originals, cinema, podcasts, animations, content creation, streaming, entertainment, storytelling, creative platform',
    };

    switch (page) {
      case '/':
        return {
          ...baseSEO,
          title: 'StrymHub - Where originals Come Alive',
          description: 'Discover captivating originals, breathtaking cinema, engaging podcasts, and stunning animations. Join StrymHub, the premier platform for content creators and storytellers worldwide.',
          type: 'website',
        };

      case '/originals':
        return {
          ...baseSEO,
          title: 'Original Content - StrymHub',
          description: 'Explore thousands of original content from talented creators worldwide. Immerse yourself in captivating narratives that come alive on StrymHub.',
          keywords: 'original content, storytelling, fiction, creative writing, narrative, reading, authors',
        };

      case '/cinema':
        return {
          ...baseSEO,
          title: 'Independent cinema & Cinema - StrymHub',
          description: 'Watch exclusive independent cinema and premium video content from emerging cinemamakers and established creators on StrymHub.',
          keywords: 'independent cinema, cinema, movies, video content, cinemamaking, streaming, documentaries',
        };

      case '/media':
        return {
          ...baseSEO,
          title: 'Media Library - cinema & Content - StrymHub',
          description: 'Browse our comprehensive media library featuring cinema, documentaries, and exclusive video content from creators around the world.',
          keywords: 'media library, cinema, videos, documentaries, content, streaming, entertainment',
        };

      case '/podcasts':
        return {
          ...baseSEO,
          title: 'Podcasts & Audio Content - StrymHub',
          description: 'Listen to engaging podcasts and audio content covering technology, creativity, storytelling, and more on StrymHub.',
          keywords: 'podcasts, audio content, tech talks, interviews, storytelling, listening, audio shows',
        };

      case '/animations':
        return {
          ...baseSEO,
          title: 'Stunning Animations & Motion Graphics - StrymHub',
          description: 'Discover breathtaking animations and motion graphics created by talented artists and animators on StrymHub.',
          keywords: 'animations, motion graphics, 2D animation, 3D animation, digital art, animated content',
        };

      case '/sneak-peeks':
        return {
          ...baseSEO,
          title: 'Behind the Scenes & Sneak Peeks - StrymHub',
          description: 'Get exclusive behind-the-scenes content and sneak peeks of upcoming projects from your favorite creators on StrymHub.',
          keywords: 'behind the scenes, sneak peeks, exclusive content, making of, creator insights',
        };

      case '/about':
        return {
          ...baseSEO,
          title: 'About StrymHub - Premier Video Streaming Platform for Content Creators',
          description: 'Learn about StrymHub, the innovative streaming platform empowering independent filmmakers, podcasters, and animators to share their creative content with a global audience.',
          keywords: 'about strymhub, video streaming platform, content creators, independent filmmakers, podcast hosting, animation platform, creative community',
        };

      case '/contact':
        return {
          ...baseSEO,
          title: 'Contact StrymHub - Get in Touch with Our Team | Support & Inquiries',
          description: 'Contact the StrymHub team for support, partnership inquiries, press requests, or general questions. We typically respond within 24-48 hours.',
          keywords: 'contact strymhub, strymhub support, customer service, help, partnership inquiry, press contact, creator support',
        };

      case '/faq':
        return {
          ...baseSEO,
          title: 'Frequently Asked Questions - StrymHub Help Center',
          description: 'Find answers to common questions about StrymHub. Learn how to create an account, upload content, monetize your videos, and more.',
          keywords: 'strymhub faq, help center, frequently asked questions, how to use strymhub, creator questions, viewer questions',
        };

      case '/privacy-policy':
        return {
          ...baseSEO,
          title: 'Privacy Policy - StrymHub',
          description: 'Learn how StrymHub protects your privacy and handles your data. Read our comprehensive privacy policy for complete transparency.',
          keywords: 'privacy policy, data protection, user privacy, terms, legal',
          noIndex: true,
        };

      case '/terms-of-service':
        return {
          ...baseSEO,
          title: 'Terms of Service - StrymHub',
          description: 'Read the StrymHub terms of service to understand your rights and responsibilities when using our content creation platform.',
          keywords: 'terms of service, user agreement, platform rules, legal terms',
          noIndex: true,
        };

      default:
        // Dynamic content pages
        if (data) {
          return this.generateDynamicSEO(page, data);
        }
        
        return {
          ...baseSEO,
          title: 'StrymHub - Where Originals Come Alive',
          description: 'Discover amazing content on StrymHub, the platform where originals come alive.',
        };
    }
  }

  private generateDynamicSEO(page: string, data: any): PageSEO {
    const baseSEO = {
      url: `${this.baseUrl}${page}`,
      keywords: 'originals, cinema, podcasts, animations, content creation, streaming, entertainment',
    };

    // cinema page
    if (page.startsWith('/cinema/') && data.cinema) {
      const cinema = data.cinema;
      return {
        ...baseSEO,
        title: `${cinema.title} - Watch on StrymHub`,
        description: cinema.description || `Watch "${cinema.title}" exclusively on StrymHub. Premium video content from talented cinemamakers.`,
        type: 'video',
        author: cinema.author?.username || cinema.author_name,
        publishedTime: cinema.created_at,
        modifiedTime: cinema.updated_at,
        tags: cinema.tags || [],
        image: cinema.thumbnail || cinema.poster,
        keywords: `${cinema.title}, cinema, movie, ${cinema.author?.username || cinema.author_name}, video content, ${(cinema.tags || []).join(', ')}`,
      };
    }

    // Podcast page
    if (page.startsWith('/podcasts/') && data.podcast) {
      const podcast = data.podcast;
      return {
        ...baseSEO,
        title: `${podcast.title} - Podcast on StrymHub`,
        description: podcast.description || `Listen to "${podcast.title}" podcast on StrymHub. Engaging audio content and conversations.`,
        type: 'audio',
        author: podcast.author?.username || podcast.author_name,
        publishedTime: podcast.created_at,
        modifiedTime: podcast.updated_at,
        tags: podcast.tags || [],
        image: podcast.cover_image || podcast.thumbnail,
        keywords: `${podcast.title}, podcast, audio, ${podcast.author?.username || podcast.author_name}, ${(podcast.tags || []).join(', ')}`,
      };
    }

    // Animation page
    if (page.startsWith('/animations/') && data.animation) {
      const animation = data.animation;
      return {
        ...baseSEO,
        title: `${animation.title} - Animation on StrymHub`,
        description: animation.description || `Watch "${animation.title}" animation on StrymHub. Stunning animated content from talented artists.`,
        type: 'video',
        author: animation.author?.username || animation.author_name,
        publishedTime: animation.created_at,
        modifiedTime: animation.updated_at,
        tags: animation.tags || [],
        image: animation.thumbnail || animation.poster,
        keywords: `${animation.title}, animation, motion graphics, ${animation.author?.username || animation.author_name}, ${(animation.tags || []).join(', ')}`,
      };
    }

    return baseSEO as PageSEO;
  }

  // Generate structured data for content
  generateStructuredData(type: string, data: any) {
    switch (type) {
      case 'story':
        return sitemapGenerator.generateArticleStructuredData({
          title: data.title,
          description: data.description || data.summary,
          author: data.author?.username || data.author_name,
          publishedTime: data.created_at,
          modifiedTime: data.updated_at,
          image: data.cover_image || data.thumbnail,
          url: `${this.baseUrl}/originals/${data.slug}`,
          tags: data.tags,
          section: 'originals'
        });

      case 'cinema':
        return sitemapGenerator.generateVideoStructuredData({
          title: data.title,
          description: data.description,
          thumbnailUrl: data.thumbnail || data.poster,
          videoUrl: data.video_file || data.video_url,
          uploadDate: data.created_at,
          duration: data.duration,
          author: data.author?.username || data.author_name
        });

      case 'podcast':
        return sitemapGenerator.generatePodcastStructuredData({
          title: data.title,
          description: data.description,
          audioUrl: data.audio_file || data.audio_url,
          publishedTime: data.created_at,
          duration: data.duration,
          author: data.author?.username || data.author_name
        });

      case 'animation':
        return sitemapGenerator.generateVideoStructuredData({
          title: data.title,
          description: data.description,
          thumbnailUrl: data.thumbnail || data.poster,
          videoUrl: data.video_file || data.video_url,
          uploadDate: data.created_at,
          duration: data.duration,
          author: data.author?.username || data.author_name
        });

      default:
        return null;
    }
  }

  // SEO-friendly URL generation
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }

  // Meta description optimization
  optimizeDescription(description: string, maxLength = 160): string {
    if (description.length <= maxLength) {
      return description;
    }
    
    const truncated = description.substring(0, maxLength - 3);
    const lastSpace = truncated.lastIndexOf(' ');
    
    return lastSpace > 0 
      ? truncated.substring(0, lastSpace) + '...'
      : truncated + '...';
  }

  // Keywords optimization
  optimizeKeywords(keywords: string[]): string {
    return keywords
      .filter(keyword => keyword.length > 0)
      .slice(0, 10) // Limit to 10 keywords
      .join(', ');
  }
}

export const seoService = new SEOService();
export default seoService;
